import type { HomePageCopy } from './homePage';

export const homePageEn: HomePageCopy = {
    hero: {
        badge: 'Inspect · Maintain · Track · Train · Report',
        title: 'The Operating System for Your Equipment Fleet',
        description:
            'Inspections, preventive maintenance, tire tracking, cost reporting, and safety training. One platform, built for crushing operations, construction crews, and equipment-heavy job sites.',
        startFreeDemo: 'Start Free Demo',
        contactUs: 'Contact Us',
        heroImageAlt: 'TotalEquipIQ platform on desktop and mobile',
    },
    trust: {
        title: 'Built for the people who actually run the equipment',
        subtitle:
            "Supervisors, operators, mechanics, and managers all work in the same system. No more spreadsheets, no more lost paperwork, no more guessing what's been serviced.",
        cards: [
            {
                title: 'Built for the Field',
                body: 'Designed for mobile use in harsh environments. Gloves, sunlight, dust, and fast inspections included.',
            },
            {
                title: 'Clear Accountability',
                body: 'Every inspection, service, and expense ties back to a user, time, and asset. Nothing falls through the cracks.',
            },
            {
                title: 'Easy to Adopt',
                body: 'Familiar workflows your crews already know. Minimal training, fast rollout, real results in weeks.',
            },
        ],
    },
    problems: {
        imageAlt: 'Field equipment management',
        title: "Equipment Management Shouldn't Live in Spreadsheets and Notebooks",
        cards: [
            {
                icon: '📄',
                title: 'Paper Gets Lost',
                body: 'Inspections, PM logs, and expense records disappear before anyone acts on them.',
            },
            {
                icon: '⏰',
                title: 'Problems Caught Too Late',
                body: "By the time the right person knows about an issue, it's already a breakdown.",
            },
            {
                icon: '📊',
                title: 'Data Lives Everywhere',
                body: 'Equipment info is in one spreadsheet, PM records in a notebook, inspections floating bills to each other.',
            },
            {
                icon: '💰',
                title: 'No Real Cost Picture',
                body: "You can't find which assets are profitable and which ones are bleeding money on repairs.",
            },
        ],
    },
    connected: {
        title: 'Everything connected, nothing siloed',
        subtitle:
            'When inspections, maintenance, costs, and training share the same data, your fleet stops fighting you.',
        cards: [
            {
                title: 'One platform, every workflow',
                body: 'Inspections, PM, tires, costs, and training all live in one system. No more reconciling spreadsheets at the end of the month.',
            },
            {
                title: 'Real-time reports',
                body: "Dashboards update as fast as your crews work. See what's due, what's overdue, and what each asset costs without waiting for someone to compile it.",
            },
            {
                title: 'Auto-flagged issues',
                body: 'Problems caught in an inspection automatically create issues, route to the right person, and tie back to the equipment that needs attention.',
            },
            {
                title: 'Permanent audit trail',
                body: 'Every inspection, service, expense, and training record ties to a user, a timestamp, and an asset. Audit-ready by default.',
            },
        ],
    },
    modules: {
        sectionTitle: 'Built for the people who actually run the equipment.',
        sectionSubtitle: "Start with the core platform. Add the modules you need, skip the ones you don't.",
        coreHeader: { highlight: 'Core', rest: ' Modules', subtitle: 'Everything below is included in every plan.' },
        learnMore: 'Learn more →',
        core: [
            {
                imageAlt: 'Inspections interface',
                label: 'Scan. Inspect. done.',
                title: 'Inspections + QR',
                body: 'Print a QR code, stick it on the machine, scan it with a phone. The right form opens to the right asset.',
                bullets: [
                    'Works with gloves and in bright sunlight',
                    'Flagged items auto-create issues',
                    'Inspector signs and submits in under 2 minutes',
                ],
            },
            {
                imageAlt: 'Equipment management',
                label: 'One source of truth for your assets and your team.',
                title: 'Equipment & Users',
                body: 'Every piece of equipment gets a profile. Every team member gets a record. Inspections, services, and training all tie back to both.',
                bullets: [
                    'Organize equipment by category, site, or status',
                    'Track UM, utilization, and lifecycle per asset',
                    'Manage operators, mechanics, and admins from one screen',
                ],
            },
            {
                imageAlt: 'Daily operations',
                label: 'Core platform, fully loaded.',
                title: 'The day-to-day, handled.',
                body: 'Daily briefing, reports, issues, forms, resources, and more, all built into the core platform. No add-ons, no upsells for the basics your operation needs every day.',
                bullets: [
                    'Daily safety briefing on user login',
                    'Issue tracking from inspection to resolution',
                    'Reports across equipment, users, and activity',
                ],
            },
        ],
        addonHeader: {
            highlight: 'Add-On',
            rest: ' Modules',
            subtitle: "Pick the ones you need. Skip the ones you don't.",
        },
        addons: [
            {
                imageAlt: 'Preventive Maintenance',
                label: 'Stop chasing PMs in a notebook.',
                title: 'Preventive Maintenance',
                body: "Set service levels, define parts and procedures, and let the system tell you what's due.",
                bullets: [
                    'Custom intervals per equipment type',
                    'Parts, vendors, and labor rates built in',
                    'Due Soon, Overdue, and Scheduled queues at a glance',
                ],
            },
            {
                imageAlt: 'Tire Management',
                label: 'Track every tire from install to replacement.',
                title: 'Tire Management',
                body: 'Build axle templates, assign tires to positions, and log tread depth on a single screen.',
                bullets: [
                    'Reusable axle templates (4x2, 6x4, 12x4, custom)',
                    'Live diagram updates as inspectors enter readings',
                    'Replacement alerts by tread percentage',
                ],
            },
            {
                imageAlt: 'Cost Tracking',
                label: 'Know what your equipment actually costs.',
                title: 'Cost Tracking',
                body: 'Log every expense against the asset that incurred it. PM, parts, labor, tires, repairs.',
                bullets: [
                    'Expense categories with vendor and invoice attachment',
                    'Internal labor rate tracking',
                    'Cost per UM reports by date range',
                ],
            },
            {
                imageAlt: 'Safety Training',
                label: 'MSHA-ready training, scheduled day by day.',
                title: 'Safety & Training',
                body: 'Build a training plan, drop topics into a 365-day schedule, and the right video shows up on the right day for each user.',
                bullets: [
                    'Daily training popups on user login',
                    'Compliance tracking by category',
                    'Permanent training records and affidavits',
                ],
            },
        ],
        coreImages: [
            '/homepage-images/inspections-QR.png',
            '/homepage-images/equipment-users.png',
            '/homepage-images/day-to-day-handled.png',
        ],
        addonImages: [
            '/homepage-images/pm-image1.png',
            '/homepage-images/tire-module-1.png',
            '/homepage-images/cost-tracking.png',
            '/homepage-images/safety-training-1.png',
        ],
    },
    pricing: {
        title: 'Pay for what you use. Add what you need.',
        body: 'Every plan includes inspections, equipment, users, and full MSHA-ready safety training. Add preventive maintenance, tire tracking, cost reporting, or activate as your operation grows. Activate them in minutes, no migration headache.',
        startFreeDemo: 'Start Free Demo',
        contactQuote: 'Contact us for a quote',
        imageAlt: 'Core platform modules',
    },
    trial: {
        title: '30 days. Your equipment. Your crews. No credit card.',
        body: 'Spin up an account in minutes. Activate the modules you want to test. See how your team uses it before you commit to anything.',
        cta: 'Start Free 30-Day Demo',
        imageAlt: 'Equipment crew',
    },
    roles: {
        title: 'Built for everyone in the operation',
        cards: [
            {
                title: 'Operator / Field Crew',
                bullets: [
                    'Scan QR + run inspection + submit quickly',
                    'See last inspection notes and known issues',
                    'Get assigned daily safety topics on login',
                ],
            },
            {
                title: 'Maintenance / Mechanics',
                bullets: [
                    'See what PMs are due and what parts are needed',
                    'Log tire inspections with live diagram updates',
                    'Track repairs and labor against the right asset',
                ],
            },
            {
                title: 'Management / Operations',
                bullets: [
                    'Audit readiness across all sites',
                    'Cost-per-UM reports by asset, category, or date range',
                    'Compliance tracking for MSHA, OSHA, and internal standards',
                ],
            },
            {
                title: 'Safety Managers',
                bullets: [
                    'Build training plans with daily topic scheduling',
                    'Track viewing records and generate affidavits',
                    'Catch recurring issues before they become incidents',
                ],
            },
        ],
    },
    testimonials: {
        label: 'Testimonials',
        title: 'What crews and supervisors say',
        ratingsLabel: '5.0 Ratings',
        authorPlaceholder: 'Author Name',
        rolePlaceholder: 'Role',
        prevAria: 'Previous',
        nextAria: 'Next',
        quotes: [
            '"We stopped losing inspections in paperwork. It\'s all visible now."',
            '"QR scan makes it simple—no one needs to \'find the right form.\'"',
        ],
    },
    faq: {
        label: 'Frequently Asked Questions',
        title: 'Straight answers for operators and managers',
        items: [
            {
                question: 'How Long Does Setup Take?',
                answer:
                    'Most companies are running inspections within a day. Full platform rollout (PM schedules, tire inventory, cost categories) typically takes one to two weeks depending on fleet size.',
            },
            {
                question: 'How Long Is The Free Demo?',
                answer:
                    '30 days, full access to the core platform. No credit card required. You can also activate any add-on modules during the trial to see if they fit your operation.',
            },
            {
                question: 'Can We Start With Just The Core And Add Modules Later?',
                answer:
                    'Yes. Every account starts with the core platform. Add-on modules can be activated or deactivated at any time from the admin panel.',
            },
            {
                question: 'Can We Create Forms For Different Equipment Types?',
                answer:
                    'Yes. Build inspection and daily service forms per equipment category and assign them to specific assets so crews always use the right checklist.',
            },
            {
                question: 'Does It Work Offline?',
                answer:
                    'Inspections can be completed offline and sync when the device reconnects. Designed for remote pits, quarries, and job sites with spotty coverage.',
            },
            {
                question: 'What About MSHA Compliance?',
                answer:
                    'The Safety & Training module is included in core and built around MSHA part 46 and 48 standards. Daily topic scheduling, affidavits, and permanent training records are all standard.',
            },
            {
                question: 'Can We Attach Photos, Notes, Or Videos?',
                answer:
                    "Yes. Every inspection item supports photos, notes, and optional audio. Files attach to the asset's permanent record.",
            },
            {
                question: 'How Does Issue Tracking Work?',
                answer:
                    "Flagged items in any inspection automatically create an issue tied to the asset, the inspector, and the timestamp. Issues route to the Issues module where they're tracked until resolved.",
            },
            {
                question: 'How Does Pricing Work?',
                answer:
                    'Core platform plus per-module add-ons. Free 30-day trial, no credit card required. Contact us for a quote based on your fleet size.',
            },
        ],
    },
    finalCta: {
        title: 'Ready to run your fleet on one system?',
        body: 'See how TotalEquipIQ connects inspections, maintenance, tires, costs, and training. Try the full platform free for 30 days, with your real equipment and your real crews.',
        startFreeDemo: 'Start Free Demo',
        contactUs: 'Contact Us',
        imageAlt: 'Equipment crew at work',
    },
};
